import { Component, OnInit, ViewContainerRef, inject } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { Meta } from '@angular/platform-browser';
import { Router, RouterOutlet } from '@angular/router';

import { environment } from '@Environment/environment';
import { isMobileDevice } from '@Login/pages/login-view/login.guard';
import { PcTabletErrorDialogComponent } from '@UIparts/common/error-dialog/pc-tablet-ver/error-dialog.component';
import { ClientSettingService } from '@VineyardLib/services/client-setting.service';
import { LOADING_SPINNER_SERVICE_TOKEN } from '@VineyardLib/services/loading-spinner-wrapper.service';
import { LoginService } from '@VineyardLib/services/login.service';
import { DATE_FNS_LOCALES } from '@VineyardLibConstant/constant';

/**
 * 起動時に読み込まれるコンポーネント
 */
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, PcTabletErrorDialogComponent],
})
export class AppComponent implements OnInit {
  private meta = inject(Meta);
  private viewContainerRef = inject(ViewContainerRef);
  private dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  private router = inject(Router);
  private clientSettingService = inject(ClientSettingService);
  private loadingSpinnerService = inject(LOADING_SPINNER_SERVICE_TOKEN);
  private loginService = inject(LoginService);

  isMobileDevice: boolean = isMobileDevice();
  /**
   * constructor
   * @param router Router
   * @param loginService LoginService
   */
  public constructor() {
    this.loginService.cookiePath = '/work';
    this.clientSettingService.initializeAppLang();

    // MatDatePickerの言語設定
    this.dateAdapter.setLocale(
      DATE_FNS_LOCALES[this.clientSettingService.locale]
    );
  }

  /**
   * 初期化
   */
  public ngOnInit(): void {
    // CSPのメタタグを追加する
    this.meta.addTag({
      httpEquiv: 'Content-Security-Policy',
      content: this.createCspContent(),
    });

    // リロード時に遷移する画面をリロードした画面ではなく作業スケジュールとしたいため、直接ログイン画面に遷移させて、ログイン画面から作業スケジュールを表示するようにする
    this.router.navigate(['login']);
    this.subscribeLoggedOut();
    if (!this.isMobileDevice) {
      this.loadingSpinnerService.attach?.(this.viewContainerRef);
    }
  }

  /**
   * ログアウトした時
   */
  private subscribeLoggedOut(): void {
    this.loginService.authenticated$.subscribe((next) => {
      if (!next) {
        this.router.navigate(['login']);
      }
    });
  }

  /**
   * CSPのメタタグに設定するポリシーを生成する
   * @returns CSPのポリシー
   */
  private createCspContent(): string {
    // Safari が CSP V2までの対応なので、 script-src-attr, script-src-elem, style-src-attr, style-src-elem は無視される。
    // Safari が CSP V3に対応するまでは、script-src, style-src で設定する。
    // script-src = script-src-attr + script-src-elem を設定
    // style-src = style-src-attr + style-src-elem を設定
    //
    // Safari で以下のコンソールエラーが発生するため、ブラウザによってポリシーを変える
    // [Unrecognized Content-Security-Policy directive 'script-src-attr'.]

    const scriptSrc = this.isSafari()
      ? "script-src 'self' 'unsafe-inline' https://maps.googleapis.com/;"
      : "script-src-attr 'unsafe-inline'; " +
        "script-src-elem 'self' https://maps.googleapis.com/;";
    const styleSrc = this.isSafari()
      ? "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com/;"
      : "style-src-attr 'unsafe-inline'; " +
        "style-src-elem 'self' 'unsafe-inline' https://fonts.googleapis.com/;";

    const connectSrc = `connect-src 'self' https://maps.googleapis.com/ ${environment.vsolServerUrl}/ ${environment.idcsOAuthUrl}/;`;
    const imgSrc =
      "img-src 'self' data: https://maps.gstatic.com/ https://*.googleapis.com/ https://*.ggpht.com;";
    const fontSrc = "font-src 'self' https://fonts.gstatic.com/;";

    return `default-src 'self'; ${scriptSrc} ${styleSrc} ${connectSrc} ${imgSrc} ${fontSrc}`;
  }

  /**
   * ブラウザがSafariか判定する
   * @returns true:safari/false:safari 以外
   * @see https://arts-factory.net/useragentbrowser/
   */
  private isSafari(): boolean {
    const agent = window.navigator.userAgent.toLowerCase();
    return agent.indexOf('chrome') == -1 && agent.indexOf('safari') > -1;
  }
}
